/**
 * v4 Ask - refined chat. AI tries first; user can escalate to accountant
 * on any reply. Suggested-question chips for low-tech users; full keyboard
 * input for everyone.
 */

import { useState } from 'react';
import { Send, User, Sparkles, ArrowUpRight, History, X, Plus, FileText, MessageCircle } from 'lucide-react';
import { useV4 } from './i18n';
import { cn } from '@/lib/utils';
import { askInConversation, listConversations, getConversation, deleteConversation, sendMessage as sendClientMessage, useClient, ChatSource, ConversationSummary } from '@/services/api/client-auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { FormattedChatMessage } from '@/components/chat/ChatRenderer';
import { toast } from 'sonner';

interface Msg {
  id: number;
  from: 'user' | 'ai';
  text: string;
  /** When set, the bubble reveals text up to this many chars (typewriter). */
  revealedChars?: number;
  forwardable?: boolean;
  forwardedToHuman?: boolean;
  sources?: ChatSource[];
}

const SUGGESTED_QS = [
  'How much profit did I make this month?',
  'When is my next BAS due?',
  'What did I spend the most on?',
  'How much do my customers owe me?',
];

interface Conversation {
  id: string;
  title: string;
  lastSnippet: string;
  when: string;
  messages: Msg[];
}

function relTime(iso: string): string {
  const d = Date.now() - new Date(iso).getTime();
  const s = Math.round(d / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s/60)}m ago`;
  if (s < 86400) return `${Math.round(s/3600)}h ago`;
  if (s < 604800) return `${Math.round(s/86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

export default function AskPageV4() {
  const { t, dir, settings } = useV4();
  const loggedInClient = useClient();
  const firmName = loggedInClient?.tenantName || 'your accountant';
  const qc = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([
    { id: 1, from: 'ai', text: t('ask_hint') },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const { data: history = [] } = useQuery({
    queryKey: ['client-conversations'],
    queryFn: () => listConversations(),
    staleTime: 30_000,
    enabled: showHistory,
  });

  const loadConversation = async (c: ConversationSummary) => {
    setShowHistory(false);
    setThinking(true);
    try {
      const detail = await getConversation(c.id);
      setConversationId(detail.id);
      setMessages([
        { id: 1, from: 'ai', text: t('ask_hint') },
        ...detail.messages.map((m, i): Msg => ({
          id: i + 2,
          from: m.role === 'user' ? 'user' : 'ai',
          text: m.content,
          forwardable: m.role === 'assistant',
          sources: m.sources || [],
        })),
      ]);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to load chat');
    } finally {
      setThinking(false);
    }
  };

  const newChat = () => {
    setConversationId(null);
    setMessages([{ id: 1, from: 'ai', text: t('ask_hint') }]);
    setShowHistory(false);
  };

  const onDeleteConversation = async (id: string) => {
    try {
      await deleteConversation(id);
      qc.invalidateQueries({ queryKey: ['client-conversations'] });
      if (id === conversationId) newChat();
      toast.success('Chat deleted');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to delete');
    }
  };

  const respond = async (userText: string, _prior: Msg[]) => {
    setThinking(true);
    try {
      const r = await askInConversation(userText, {
        conversationId: conversationId || undefined,
        lang: settings.lang || undefined,
      });
      setConversationId(r.conversationId);
      qc.invalidateQueries({ queryKey: ['client-conversations'] });
      const fullText = r.message?.content || '';
      const newId = Date.now();
      setMessages(m => [...m, {
        id: newId,
        from: 'ai',
        text: fullText,
        revealedChars: 0,
        forwardable: false, // becomes true when reveal completes
        sources: r.sources || r.message?.citations || [],
      }]);
      // Typewriter reveal: ~80 chars per 40ms tick = ~2000 chars/sec
      const STEP_MS = 40;
      const CHARS_PER_STEP = Math.max(20, Math.ceil(fullText.length / 60));
      const interval = setInterval(() => {
        setMessages(prev => {
          const idx = prev.findIndex(m => m.id === newId);
          if (idx === -1) { clearInterval(interval); return prev; }
          const cur = prev[idx];
          const next = Math.min((cur.revealedChars || 0) + CHARS_PER_STEP, fullText.length);
          if (next >= fullText.length) {
            clearInterval(interval);
            const updated = [...prev];
            updated[idx] = { ...cur, revealedChars: fullText.length, forwardable: true };
            return updated;
          }
          const updated = [...prev];
          updated[idx] = { ...cur, revealedChars: next };
          return updated;
        });
      }, STEP_MS);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to get a response';
      toast.error(msg);
      setMessages(m => [...m, {
        id: Date.now(),
        from: 'ai',
        text: 'Sorry, I couldn\'t answer that just now. Please try again or ask your accountant.',
        forwardable: true,
      }]);
    } finally {
      setThinking(false);
    }
  };

  const send = (txt: string) => {
    const text = txt.trim();
    if (!text) return;
    const userMsg: Msg = { id: Date.now(), from: 'user', text };
    setMessages(m => {
      const next = [...m, userMsg];
      respond(text, m);
      return next;
    });
    setInput('');
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const forward = async (id: number) => {
    const aiMsg = messages.find(m => m.id === id);
    if (!aiMsg) return;
    const aiIdx = messages.findIndex(m => m.id === id);
    const userMsg = [...messages].slice(0, aiIdx).reverse().find(m => m.from === 'user');

    // Optimistic UI
    setMessages(m => m.map(msg => msg.id === id ? { ...msg, forwardedToHuman: true, forwardable: false } : msg));

    const body =
      `[Forwarded from AI chat]\n\n` +
      (userMsg ? `My question:\n${userMsg.text}\n\n` : '') +
      `What the AI said:\n${aiMsg.text}\n\n` +
      `I'd like your take on this when you have a moment.`;

    try {
      await sendClientMessage(body);
      setMessages(m => [...m, { id: Date.now(), from: 'ai', text: t('forwarded_to_accountant') }]);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not forward to your accountant');
      // Rollback the optimistic mark
      setMessages(m => m.map(msg => msg.id === id ? { ...msg, forwardedToHuman: false, forwardable: true } : msg));
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="pb-3 pt-1 flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('ask_title')}</p>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t('ask_hint')}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={newChat}
            title="New chat"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors"
          >
            <Plus className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button
            onClick={() => setShowHistory(true)}
            title="Chat history"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors"
          >
            <History className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-2">
        {messages.map(msg => (
          <div key={msg.id} className={cn('flex', msg.from === 'user' ? 'justify-end' : 'justify-start')}>
            <div className={cn('flex flex-col gap-1.5 w-full', msg.from === 'user' ? 'items-end' : 'items-start')}>
              <div
                // `dir="auto"` lets the browser pick text direction from the
                // first strong directional character in the bubble. Persian,
                // Arabic, Urdu, Hebrew etc. flip the text inside the bubble
                // to RTL automatically without touching the page layout.
                dir="auto"
                className={cn(
                  'max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                  msg.from === 'user'
                    ? 'bg-[var(--v4-accent)] text-white rounded-br-md'
                    : 'bg-card border border-border text-foreground rounded-bl-md',
                  dir === 'rtl' && msg.from === 'user' && 'rounded-bl-md rounded-br-2xl',
                  dir === 'rtl' && msg.from === 'ai' && 'rounded-br-md rounded-bl-2xl'
                )}
              >
                {msg.from === 'ai'
                  ? (() => {
                      const isRevealing = msg.revealedChars !== undefined && msg.revealedChars < msg.text.length;
                      const shown = msg.revealedChars !== undefined ? msg.text.slice(0, msg.revealedChars) : msg.text;
                      return (
                        <>
                          <FormattedChatMessage content={shown} />
                          {isRevealing && <span className="inline-block w-1.5 h-3.5 bg-current ml-0.5 animate-pulse align-middle" />}
                        </>
                      );
                    })()
                  : msg.text}
              </div>
              {msg.from === 'ai' && msg.sources && msg.sources.length > 0 && (msg.revealedChars === undefined || msg.revealedChars >= msg.text.length) && (
                <div className="max-w-[92%] flex flex-wrap gap-1.5">
                  {msg.sources.slice(0, 6).map((s, i) => (
                    <span
                      key={i}
                      title={s.excerpt}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-border bg-card text-[10px] text-muted-foreground"
                    >
                      <FileText className="w-2.5 h-2.5" />
                      {s.documentName || s.title || 'source'}
                    </span>
                  ))}
                </div>
              )}
              {msg.forwardable && !msg.forwardedToHuman && (
                <button
                  onClick={() => forward(msg.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80 transition-colors hover:border-muted-foreground/60 hover:text-foreground"
                >
                  <User className="h-3 w-3" />
                  {t('ask_marcus_instead', { firm: firmName })}
                  <ArrowUpRight className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}

        {thinking && (
          <div className="flex">
            <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-border bg-card px-3.5 py-3">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" style={{ animationDelay: '150ms' }} />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {/* Suggested questions chips - only on first turn */}
        {messages.length === 1 && (
          <div className="pt-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60 mb-2">Try asking</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_QS.map(q => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-[12px] text-foreground/80 hover:border-muted-foreground/40 hover:text-foreground transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Composer - pinned at the bottom of the flex column (not fixed, so it
          never overlaps the bottom nav on iOS). */}
      <form onSubmit={onSubmit} className="shrink-0 pt-2">
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-1 shadow-md">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={t('type_question')}
            className="flex-1 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            aria-label={t('send')}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--v4-accent)] text-white shadow-sm transition-all hover:bg-[var(--v4-accent-hover)] disabled:bg-muted disabled:text-muted-foreground"
          >
            <Send className={cn('h-4 w-4', dir === 'rtl' && '-scale-x-100')} />
          </button>
        </div>
      </form>

      {/* Chat history sheet */}
      {showHistory && (
        <>
          <div onClick={() => setShowHistory(false)} className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200" />
          <div className="fixed inset-x-0 bottom-0 top-12 z-50 mx-auto flex max-w-[720px] flex-col overflow-hidden rounded-t-2xl border-t border-border bg-background shadow-2xl animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-[var(--v4-accent-text)]" strokeWidth={1.75} />
                <h2 className="text-sm font-bold">Chat history</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={newChat}
                  className="inline-flex items-center gap-1 rounded-md bg-[var(--v4-accent)] px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
                >
                  <Plus className="h-3 w-3" /> New chat
                </button>
                <button onClick={() => setShowHistory(false)} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {history.length === 0 ? (
                <div className="px-6 py-12 text-center text-sm text-muted-foreground">
                  No saved chats yet - start a new conversation.
                </div>
              ) : (
                <ul className="divide-y divide-border/60">
                  {history.map(c => (
                    <li key={c.id} className="group relative">
                      <button
                        onClick={() => loadConversation(c)}
                        className={cn(
                          "flex w-full items-start gap-3 px-4 py-3 text-start transition-colors hover:bg-muted/40",
                          c.id === conversationId && 'bg-[var(--v4-accent-tint)]'
                        )}
                      >
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-[var(--v4-accent-tint)] text-[var(--v4-accent-text)]">
                          <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="text-sm font-semibold text-foreground truncate">{c.title}</p>
                            <span className="text-[10px] text-muted-foreground flex-shrink-0">{relTime(c.updatedAt)}</span>
                          </div>
                          {c.lastSnippet && (
                            <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2 leading-snug">{c.lastSnippet}</p>
                          )}
                        </div>
                      </button>
                      <button
                        onClick={() => { if (confirm('Delete this chat?')) onDeleteConversation(c.id); }}
                        className="absolute top-3 right-3 flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-rose-100 hover:text-rose-600 dark:hover:bg-rose-950/30 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Delete chat"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function canned(q: string): string {
  const lower = q.toLowerCase();
  if (lower.includes('tax') || lower.includes('bas') || lower.includes('مالی')) {
    return 'Your next BAS is due in 12 days (Q3 2026). The draft total is $7,820. Marcus has prepared it for you - you will receive a notification once it is ready to lodge.';
  }
  if (lower.includes('profit') || lower.includes('made') || lower.includes('پول')) {
    return 'This month you made $21,240 in revenue. After $15,870 of expenses, your profit is $5,370 - a 25.3% margin. That is +8% compared to last month.';
  }
  if (lower.includes('expense') || lower.includes('spent')) {
    return 'This month your largest expense was Materials ($8,920 - 56% of total). Then Rent ($2,800), then Wages ($2,100).';
  }
  if (lower.includes('owe') || lower.includes('customer') || lower.includes('invoice')) {
    return 'You have $12,480 in unpaid invoices - three customers. The oldest is 28 days. Marcus can chase them if you would like.';
  }
  return 'I am not sure about that one. Tap "Ask Marcus instead" and your accountant will help.';
}
